/**
 * Per-message file context for chats.
 *
 * A member can pin extra files into a message as context:
 *   - the file currently open in the side pane (auto-added)
 *   - any file they `@`-mention in the composer (manual)
 *
 * At submit time the composer reads each file through the host's
 * {@link FileContextProvider} and inlines its contents into the user message as
 * a *hidden* leading text part wrapped in `<workspace_context>` markers. The
 * agent sees the full context; the transcript strips that part from the bubble
 * and shows a chip list instead.
 *
 * Keeping the context inline on the persisted message means the server needs no
 * new knowledge, a reload re-renders the same context the agent saw, and the
 * snapshot is taken at send time — editing the file later doesn't rewrite what
 * the model was looking at.
 */
import type { TreeEntry } from "playground/fs/paths";
import { isTextMime } from "playground/fs/text-detection";
import type { Playground } from "playground/hal/index";
import { useEffect, useMemo, useState } from "react";

import type { FileContextProvider } from "./types";
import {
  CONTEXT_CLOSE_TAG,
  CONTEXT_OPEN_TAG,
  isContextTextPart,
} from "./workspace-context";

export {
  CONTEXT_CLOSE_TAG,
  CONTEXT_OPEN_TAG,
  isContextTextPart,
} from "./workspace-context";

export interface ContextFile {
  path: string;
  content: string;
}

/**
 * Every text file a member can `@`-mention, alpha-sorted.
 *
 * Read through the HAL, which is what crosses a mount: `listFiles` materializes
 * mounted units (a project's `node_modules/<pkg>`) and `open` reads across one.
 * Walking the project's inode map directly and keeping only `automerge:` uris —
 * which is what this did when it lived in Hearth, where nothing was mounted —
 * is silently blind to every mounted file (AGENTS.md § Published packages).
 *
 * MIME decides what's offered, not the uri: a mounted `.ts` is as mentionable as
 * a local one, and a PDF is mentionable through neither (the composer inlines
 * text, and `read` below returns null for anything else).
 */
export function listMentionablePaths(entries: TreeEntry[]): string[] {
  return entries
    .filter((e) => !e.isDirectory && isTextMime(e.inode.mimeType))
    .map((e) => e.path)
    .sort((a, b) => a.localeCompare(b));
}

/**
 * The provider both hosts want: their workspace, through its Playground.
 *
 * `fsRevision` advances whenever the filesystem changes (`useTreeRevision`,
 * which also sees nested and linked directories). The listing is re-read
 * whenever it changes rather than once on mount: a stale mention list is a
 * dangling chip.
 *
 * This walks the WHOLE tree, mounts included, which the file tree deliberately
 * does not (see `file-tree/use-materialized-files.ts`: it materializes only
 * what the user expanded). The difference is that a tree renders what's on
 * screen while autocomplete has to match against everything, so there is no
 * expansion to key on. It's affordable because a mount today is a published
 * unit: one immutable manifest describes its whole subtree and the playground
 * caches it per base URL for the instance's lifetime, so a re-walk costs
 * nothing after the first. **A mount backend that lists on demand under a TTL
 * would change that**, and this is the callsite that would have to become
 * lazy — the tree's warning applies here too, it just doesn't bite yet.
 */
export function usePlaygroundFileContext(
  playground: Playground | null,
  fsRevision: unknown,
): FileContextProvider | null {
  const [paths, setPaths] = useState<string[]>([]);

  useEffect(() => {
    if (!playground) {
      setPaths([]);
      return;
    }
    let live = true;
    void playground
      .listFiles(undefined, { recursive: true })
      .then((entries) => {
        if (live) setPaths(listMentionablePaths(entries));
      })
      .catch(() => {
        if (live) setPaths([]);
      });
    return () => {
      live = false;
    };
  }, [playground, fsRevision]);

  return useMemo(
    () =>
      playground
        ? {
            paths,
            read: async (path: string) => {
              // A path can go stale between listing and send (rename, delete),
              // and a mount read can fail on the network. Either way the
              // composer drops that snapshot and sends the rest.
              const file = await playground.open(path).catch(() => null);
              return file?.kind === "text" ? file.read() : null;
            },
          }
        : null,
    [playground, paths],
  );
}

/** Wrap a set of file snapshots as the hidden leading text part of a user message. */
export function formatContextBlock(files: ContextFile[]): string {
  if (files.length === 0) return "";
  const body = files
    .map((f) => `## File: ${f.path}\n\n${f.content}`)
    .join("\n\n");
  return `${CONTEXT_OPEN_TAG}\n${body}\n${CONTEXT_CLOSE_TAG}`;
}

/** Recover the list of referenced paths from a stored context block. */
export function parseContextPaths(text: string): string[] {
  if (!isContextTextPart(text)) return [];
  const out: string[] = [];
  const re = /^## File: (.+)$/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const p = m[1]?.trim();
    if (p) out.push(p);
  }
  return out;
}

export interface MentionTrigger {
  /** Index of the `@` character. */
  start: number;
  /** Index just past the last character of the query (== caret). */
  end: number;
  /** Query string (text between `@` and the caret). May be empty. */
  query: string;
}

/**
 * Detect an active `@`-mention at the caret. The trigger fires when an `@`
 * sits at start-of-text or right after whitespace, and the run from `@` up
 * to the caret contains no whitespace. Returns null when no trigger is in
 * effect (typical cursor positions in body text).
 */
export function detectMentionTrigger(
  text: string,
  caret: number,
): MentionTrigger | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "@") {
      const prev = i > 0 ? text[i - 1] : undefined;
      if (prev !== undefined && !/\s/.test(prev)) return null;
      return { start: i, end: caret, query: text.slice(i + 1, caret) };
    }
    if (ch !== undefined && /\s/.test(ch)) return null;
  }
  return null;
}

/** Rank suggestions: exact prefix on basename first, then prefix on path, then contains. */
export function filterMentionSuggestions(
  paths: string[],
  query: string,
  limit = 8,
): string[] {
  const q = query.toLowerCase();
  if (q.length === 0) return paths.slice(0, limit);
  const scored: { path: string; score: number }[] = [];
  for (const p of paths) {
    const lower = p.toLowerCase();
    const base = lower.split("/").pop() ?? lower;
    let score: number;
    if (base.startsWith(q)) score = 0;
    else if (lower.startsWith(q)) score = 1;
    else if (base.includes(q)) score = 2;
    else if (lower.includes(q)) score = 3;
    else continue;
    scored.push({ path: p, score });
  }
  scored.sort((a, b) => a.score - b.score || a.path.localeCompare(b.path));
  return scored.slice(0, limit).map((s) => s.path);
}
